import React, { useState, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { STATUS, SWAL_SETTINGS, THEME_COLORS } from "../../../../../utils/Constants";
import {
  TrimText,
  handleServerValidations,
  hasP,
} from "../../../../../utils/commonfunction";
import * as consentServices from "../../../services/consent.services";
import { useTranslation } from "react-i18next";
import { CustomProvider, DateRangePicker, Placeholder, Tooltip, Whisper } from "rsuite";
import { useQuery } from "react-query";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import moment from "moment";
import NoRowsOverlay from "../../../common/NoRowsOverlay";

ModuleRegistry.registerModules([AllCommunityModule]);

const ConsentTable = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const gridRefHistory = useRef();

  const [searchData, setSearchData] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [activeTab, setActiveTab] = useState("documents");
  const [perPage, setPerPage] = useState(10);

  const location = useLocation();
  const isDevMode = new URLSearchParams(location.search).get('dev') === 'true';

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "User Consents", url: "" },
  ];

  const getConsentTypeLabel = (type) => {
    switch (type) {
      case "PARENT_LINK_CONSENT":
        return "Parent Link Consent";
      case "PARENT_LINK_CONSENT_TEEN":
        return "Parent Link Consent Teen";
      case "SELF_CONSENT_17":
        return "Self Consent 17";
      case "ADULT_CONSENT_18_PLUS":
        return "Adult Consent 18 Plus";
      case "MARKETING":
        return "Marketing Consent";
      case "CLUB_ADMIN_CONSENT":
        return "Club Admin Consent";
      case "PARENT_SELF_CONSENT":
        return "Parent Self Consent";
      case "PRIVACY_POLICY":
        return "Privacy Policy";
      case "TERMS_CONDITIONS":
        return "Terms Conditions";
      case "DATA_PROTECTION":
        return "Data Protection";
      case "SAFE_GUARD_POLICY":
        return "Safe Guard Policy";
      default:
        return type || "N/A";
    }
  };

  const getConsentRoleLabel = (role) => {
    if (!role) return "N/A";
    const normalized = role.toString().toLowerCase().trim().replace(/[-_]/g, "_");
    if (
      normalized === "non_affiliated_user" ||
      normalized === "non_affiliated" ||
      normalized === "nonaffiliated" ||
      normalized === "basic_user" ||
      normalized === "basic"
    ) {
      return "Basic User";
    }
    if (normalized === "club_admin") return "Club Admin";
    if (normalized === "super_admin") return "Super Admin";
    if (normalized === "sub_admin") return "Sub Admin";
    if (normalized === "team_manager") return "Team Manager";
    if (normalized === "player") return "Player";
    if (normalized === "parent") return "Parent";

    return role
      .toString()
      .replace(/[-_]/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };


  const { data, isLoading, refetch } = useQuery({
    queryKey: ["consent-list"],
    queryFn: () => consentServices.List({
      limit: 1000,
    }),
    refetchOnWindowFocus: false,
    retry: false,
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const { data: historyData, isLoading: isHistoryLoading, refetch: refetchHistory } = useQuery({
    queryKey: ["consent-history-list"],
    queryFn: () => consentServices.History({
      limit: 1000,
    }),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: activeTab === "history",
    onError: (error) => {
      Swal.fire({
        icon: "error",
        text: handleServerValidations(error),
        ...SWAL_SETTINGS,
      });
    },
  });

  const changeStatus = (id, status) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        if (gridRef.current?.api) {
          const rowNode = gridRef.current.api.getRowNode(id);
          if (rowNode) {
            rowNode.setDataValue('status', status);
          }
        }

        consentServices.Status({ status }, id).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || "Status updated successfully",
              ...SWAL_SETTINGS,
            });
          } else {
            if (gridRef.current?.api) {
              const rowNode = gridRef.current.api.getRowNode(id);
              if (rowNode) {
                rowNode.setDataValue('status', status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE);
              }
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(id);
            if (rowNode) {
              rowNode.setDataValue('status', status === STATUS.ACTIVE ? STATUS.INACTIVE : STATUS.ACTIVE);
            }
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const deleteRecord = (row) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: THEME_COLORS.ADMIN_PRIMARY,
      cancelButtonColor: "#000",
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        const rowId = row.originalId;

        if (gridRef.current?.api) {
          gridRef.current.api.applyTransaction({
            remove: [{ originalId: rowId }]
          });
          gridRef.current.api.refreshCells({ force: true });
        }

        consentServices.Delete(rowId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || "Deleted successfully",
              ...SWAL_SETTINGS,
              timer: 1500,
              showConfirmButton: false
            });
          } else {
            if (gridRef.current?.api) {
              gridRef.current.api.applyTransaction({
                add: [row]
              });
            }
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          if (gridRef.current?.api) {
            gridRef.current.api.applyTransaction({
              add: [row]
            });
          }
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };


  const handleWithdrawConsent = (row) => {
    const userName = row.userName || "this user";
    const userRole = row.roleLabel || "User";
    const consentType = row.consentTypeLabel || "Consent";

    Swal.fire({
      customClass: "swal-wide",
      title: "Withdraw Consent?",
      html: `
        <div style="text-align: left; font-size: 14px; color: #495057;">
          <p>Are you sure you want to withdraw <strong>${consentType}</strong> for <strong>${userName}</strong> (${userRole})?</p>
          <div class="alert alert-danger mb-0 mt-3" style="font-size: 13px; border-radius: 8px;">
            <i class="ri-alert-line me-1"></i>
            <strong>Warning:</strong> Withdrawing consent will mark the consent as withdrawn and downgrade the ${userRole.toLowerCase()}'s account to a basic non-affiliated user.
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Withdraw Consent",
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        consentServices.Withdraw(row.originalId).then((response) => {
          if (response.success) {
            Swal.fire({
              icon: "success",
              text: response.message || "Consent withdrawn and account downgraded successfully.",
              ...SWAL_SETTINGS,
            });
            refetchHistory();
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        }).catch((error) => {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(error),
            ...SWAL_SETTINGS,
          });
        });
      }
    });
  };

  const colDefs = useMemo(() => [
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: "Title",
      field: "title",
      flex: 1,
      minWidth: 200,
      tooltipField: "title",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      ),
    },
    {
      headerName: "Consent Type",
      field: "typeLabel",
      minWidth: 180,
      tooltipField: "typeLabel",
      cellRenderer: (params) => (
        <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value || "N/A"}
        </div>
      ),
    },
    {
      headerName: "Version",
      field: "version",
      maxWidth: 100,
      minWidth: 80,
      cellStyle: {
        display: "flex",
        justifyContent: "center"
      },
      cellRenderer: (params) => (
        <span className="badge badge-pill badge-primary-light" style={{ fontSize: "12px" }}>
          {params.value || "N/A"}
        </span>
      ),
    },
    {
      headerName: t("list_heading_status"),
      field: "status",
      filter: true,
      maxWidth: 90,
      cellStyle: {
        display: "flex",
        justifyContent: "center"
      },
      cellRenderer: (params) => {
        const isActive = params.value === STATUS.ACTIVE;
        return (
          <label className={`custom-switch cp${!isActive ? " e_none" : ""}`}>
            <input
              type="checkbox"
              className="custom-switch-input cp"
              checked={isActive}
              onChange={() => {
                const newStatus = isActive ? STATUS.INACTIVE : STATUS.ACTIVE;
                changeStatus(params.data.originalId, newStatus);
              }}
            />
            <span className="custom-switch-indicator cp"></span>
          </label>
        );
      },
    },
    {
      headerName: t("list_heading_created_date"),
      field: "createdAt",
      filter: "agDateColumnFilter",
      minWidth: 150,
      maxWidth: 150,
      valueGetter: (params) =>
        params.data?.createdAt ? new Date(params.data.createdAt) : null,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("MMMM DD, YYYY") : ""
    },
    {
      headerName: t("list_heading_action"),
      maxWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const row = params.data;
        return (
          <div className="btn-toolbar table-actions">
            <div className="btn-group btn-group-sm">
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>View</Tooltip>}
              >
                <button
                  type="button"
                  className="btn view"
                  onClick={() => navigate(`/admin/cms/consent/view/${row.originalId}`)}
                >
                  <i className="ri-eye-fill"></i>
                </button>
              </Whisper>

              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Edit</Tooltip>}
              >
                <button
                  type="button"
                  className="btn edit"
                  onClick={() => navigate(`/admin/cms/consent/edit/${row.originalId}`)}
                >
                  <i className="ri-pencil-fill"></i>
                </button>
              </Whisper>

              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip style={{ fontWeight: 'bold' }}>Delete</Tooltip>}
              >
                <button
                  type="button"
                  className="btn delete"
                  onClick={() => deleteRecord(row)}
                >
                  <i className="ri-delete-bin-6-fill"></i>
                </button>
              </Whisper>
            </div>
          </div>
        );
      },
    }
  ], [t, navigate]);

  const rowData = useMemo(() => {
    if (!data?.data?.list) return [];

    return data.data.list
      .map((row) => ({
        originalId: row.id,
        title: row.title ? TrimText(row.title, 70) : "N/A",
        type: row.type || "N/A",
        typeLabel: getConsentTypeLabel(row.type),
        version: row.version || "N/A",
        status: row.status,
        createdAt: row.createdAt,
      }))
      .filter((row) =>
        searchData
          ? row.title.toLowerCase().includes(searchData.toLowerCase()) ||
          row.typeLabel.toLowerCase().includes(searchData.toLowerCase()) ||
          row.version.toLowerCase().includes(searchData.toLowerCase())
          : true
      );
  }, [data?.data?.list, searchData]);

  const getAgGridConfig = useMemo(() => ({
    ref: gridRef,
    domLayout: "autoHeight",
    rowData: rowData,
    columnDefs: colDefs,
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    getRowId: (params) => params.data.originalId.toString(),
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 150,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
  }), [rowData, colDefs]);

  const colDefsHistory = useMemo(() => [
    {
      headerName: t("list_heading_sno"),
      field: "srNo",
      maxWidth: 85,
      sortable: false,
      filter: false,
      suppressMovable: true,
      lockPosition: true,
      valueGetter: (params) => {
        if (!params.node || params.node.rowIndex === undefined) return "";
        return params.node.rowIndex + 1;
      },
      cellStyle: { borderRight: "1px solid #dee2e6", color: "#3c4858" },
    },
    {
      headerName: "User Name",
      field: "userName",
      minWidth: 150,
      tooltipField: "userName",
      cellRenderer: (params) => (
        <div style={{ fontWeight: "600", color: "#2c3033" }}>
          {params.value || "N/A"}
        </div>
      ),
    },
    {
      headerName: "User Email",
      field: "userEmail",
      minWidth: 180,
      tooltipField: "userEmail",
    },
    {
      headerName: "Role",
      field: "roleLabel",
      maxWidth: 130,
      cellRenderer: (params) => {
        let badgeClass = "badge-light";
        const val = params.value;
        if (val === "Player") badgeClass = "badge-primary-light";
        else if (val === "Parent") badgeClass = "badge-warning-light";
        else if (val === "Basic User") badgeClass = "badge-info-light";
        else if (val === "Club Admin" || val?.toLowerCase()?.includes("admin")) badgeClass = "badge-danger-light";
        else if (val === "Team Manager") badgeClass = "badge-teal-light";
        return (
          <span className={`badge badge-pill ${badgeClass}`} style={{ fontSize: "11px" }}>
            {val || "N/A"}
          </span>
        );
      }
    },
    {
      headerName: "Consent Type",
      field: "consentTypeLabel",
      minWidth: 180,
      tooltipField: "consentTypeLabel",
    },
    {
      headerName: "Version",
      field: "version",
      maxWidth: 100,
      minWidth: 80,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: (params) => (
        <span className="badge badge-pill badge-primary-light" style={{ fontSize: "12px" }}>
          {params.value || "N/A"}
        </span>
      ),
    },
    {
      headerName: "Date Granted",
      field: "grantedAt",
      filter: "agDateColumnFilter",
      minWidth: 160,
      valueGetter: (params) =>
        params.data?.grantedAt ? new Date(params.data.grantedAt) : null,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("MMMM DD, YYYY hh:mm A") : ""
    },
    {
      headerName: "Withdrawn At",
      field: "withdrawnAt",
      filter: "agDateColumnFilter",
      minWidth: 160,
      valueGetter: (params) =>
        params.data?.withdrawnAt ? new Date(params.data.withdrawnAt) : null,
      valueFormatter: (params) =>
        params.value ? moment(params.value).format("MMMM DD, YYYY hh:mm A") : "N/A"
    },
    {
      headerName: "IP Address",
      field: "ipAddress",
      minWidth: 130,
      cellStyle: { fontFamily: "monospace", fontSize: "12px" }
    },
    {
      headerName: "Consenting Parent",
      field: "consentingParent",
      minWidth: 200,
      tooltipField: "consentingParent",
      cellRenderer: (params) => (
        <span style={{ fontSize: "13px" }}>
          {params.value || "N/A"}
        </span>
      )
    },
    {
      headerName: "Child Username",
      field: "childUsername",
      minWidth: 160,
      tooltipField: "childUsername",
      cellRenderer: (params) => (
        <span style={{ fontSize: "13px", fontWeight: "500", color: "#2c3033" }}>
          {params.value || "N/A"}
        </span>
      )
    },
    {
      headerName: "Consent Method",
      field: "consentMethod",
      minWidth: 140,
      cellRenderer: (params) => {
        const isSelf = params.value?.includes("Self");
        return (
          <span style={{ fontWeight: "500", color: isSelf ? THEME_COLORS.ADMIN_PRIMARY : "#ebb322" }}>
            {params.value || "N/A"}
          </span>
        );
      }
    },
    {
      headerName: "DOB Verified",
      field: "dobVerified",
      minWidth: 130,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: (params) => {
        const isVerified = params.value === true;
        return (
          <span className={`badge badge-pill ${isVerified ? "badge-success-light" : "badge-light"}`} style={{ fontSize: "11px" }}>
            {isVerified ? "Yes" : "No"}
          </span>
        );
      }
    },
    {
      headerName: "Status at Consent",
      field: "accountStatus",
      minWidth: 150,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: (params) => {
        const status = params.value || "N/A";
        const isActive = status.toUpperCase() === "ACTIVE";
        return (
          <span className={`badge badge-pill ${isActive ? "badge-success-light" : "badge-danger-light"}`} style={{ fontSize: "11px" }}>
            {status}
          </span>
        );
      }
    },
    {
      headerName: "Action",
      pinned: "right",
      maxWidth: 140,
      minWidth: 130,
      sortable: false,
      filter: false,
      lockPinned: true,
      cellStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
      cellRenderer: (params) => {
        const row = params.data;
        const isWithdrawn = !!row.withdrawnAt;

        if (isWithdrawn) {
          return (
            <span
              className="badge badge-pill badge-light"
              style={{
                fontSize: "11px",
                color: "#8c98a5",
                border: "1px solid #dee2e6",
                padding: "4px 10px",
                display: "inline-flex",
                alignItems: "center",
                fontWeight: "600"
              }}
            >
              <i className="ri-close-circle-line me-1 text-muted" style={{ fontSize: "13px" }}></i>
              Withdrawn
            </span>
          );
        }

        return (
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip style={{ fontWeight: 'bold' }}>Withdraw Consent & Downgrade</Tooltip>}
          >
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              style={{
                borderRadius: "20px",
                padding: "4px 12px",
                fontSize: "12px",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                lineHeight: "1.4",
                boxShadow: "0 1px 3px rgba(220, 53, 69, 0.2)"
              }}
              onClick={() => handleWithdrawConsent(row)}
            >
              <i className="ri-indeterminate-circle-line" style={{ fontSize: "13px" }}></i>
              Withdraw
            </button>
          </Whisper>
        );
      },
    }
  ], [t]);

  const rowDataHistory = useMemo(() => {
    if (!historyData?.data?.list) return [];

    return historyData.data.list
      .map((row) => ({
        originalId: row.id,
        userName: row.user ? `${row.user.firstName} ${row.user.lastName || ""}`.trim() : "N/A",
        userEmail: row.user?.email || "N/A",
        roleLabel: row.user?.role ? getConsentRoleLabel(row.user.role) : "N/A",
        consentType: row.consent_type || "N/A",
        consentTypeLabel: getConsentTypeLabel(row.consent_type),
        version: row.consent_version || "N/A",
        grantedAt: row.consent_granted_at || row.createdAt,
        ipAddress: row.ip_address_of_consent || "N/A",
        consentingParent: row.consentingParent ? `${row.consentingParent.firstName} ${row.consentingParent.lastName || ""}`.trim() + ` (${row.consentingParent.email})` : "",
        childUsername: row.childUsername || (row.children && row.children.length > 0 ? row.children.map(c => c.username || `${c.firstName} ${c.lastName || ""}`.trim()).join(", ") : "N/A"),
        consentMethod: row.consent_method === "SELF_CONSENT_IN_APP" ? "Self (In-App)" : "Parent Link",
        withdrawnAt: row.consent_withdrawn_at || null,
        dobVerified: row.player_dob_verified,
        accountStatus: row.account_status_at_consent || "N/A",
      }))
      .filter((row) =>
        historySearch
          ? row.userName.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.userEmail.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.roleLabel.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.consentTypeLabel.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.version.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.ipAddress.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.consentingParent.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.childUsername.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.consentMethod.toLowerCase().includes(historySearch.toLowerCase()) ||
          row.accountStatus.toLowerCase().includes(historySearch.toLowerCase())
          : true
      );
  }, [historyData?.data?.list, historySearch]);

  const getAgGridConfigHistory = useMemo(() => ({
    ref: gridRefHistory,
    domLayout: "autoHeight",
    rowData: rowDataHistory,
    columnDefs: colDefsHistory,
    noRowsOverlayComponent: NoRowsOverlay,
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50, 100],
    suppressCellFocus: true,
    getRowId: (params) => params.data.originalId.toString(),
    defaultColDef: {
      resizable: true,
      sortable: true,
      minWidth: 120,
      cellStyle: {
        padding: "10px 8px",
      },
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    },
    headerHeight: 50,
    rowHeight: 45,
  }), [rowDataHistory, colDefsHistory]);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <div className="animation_fade">
        <div className="d-flex mb-4" style={{ gap: "12px" }}>
          <button
            onClick={() => setActiveTab("documents")}
            className={`btn px-4 py-2 d-flex align-items-center transition-all ${
              activeTab === "documents"
                ? "btn-main-primary shadow"
                : "btn-light text-muted"
            }`}
            style={{
              borderRadius: "30px",
              fontWeight: "600",
              fontSize: "14px",
              border: activeTab === "documents" ? "none" : "1px solid #e8e8f7",
              boxShadow: activeTab === "documents" ? "0 4px 15px rgba(98, 89, 202, 0.3)" : "none",
            }}
          >
            <i className="ri-file-shield-2-line me-2" style={{ fontSize: "16px" }}></i>
            Consent Documents
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`btn px-4 py-2 d-flex align-items-center transition-all ${
              activeTab === "history"
                ? "btn-main-primary shadow"
                : "btn-light text-muted"
            }`}
            style={{
              borderRadius: "30px",
              fontWeight: "600",
              fontSize: "14px",
              border: activeTab === "history" ? "none" : "1px solid #e8e8f7",
              boxShadow: activeTab === "history" ? "0 4px 15px rgba(98, 89, 202, 0.3)" : "none",
            }}
          >
            <i className="ri-history-line me-2" style={{ fontSize: "16px" }}></i>
            Consent History Audit Trail
          </button>
        </div>

        <div className="card custom-card overflow-hidden">
          {activeTab === "documents" ? (
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ borderBottom: "none" }}
            >
              <h6 className="main-content-label">User Consent Documents</h6>
              <div className="d-flex align-items-center">
                <div style={{ width: 300 }} className="me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search title, type or version..."
                    value={searchData}
                    onChange={(e) => setSearchData(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>
                <select
                  className="form-select form-control me-3 cp"
                  style={{ width: 150 }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (gridRef.current && gridRef.current.api) {
                      if (value && value.length) {
                        gridRef.current.api.setFilterModel({
                          status: {
                            type: "equals",
                            filter: value,
                          }
                        });
                      } else {
                        gridRef.current.api.setFilterModel(null);
                      }
                      gridRef.current.api.onFilterChanged();
                    }
                  }}
                >
                  <option value="">{t("list_heading_status")}</option>
                  <option value={STATUS.ACTIVE}>{t("btn_active")}</option>
                  <option value={STATUS.INACTIVE}>{t("btn_inactive")}</option>
                </select>

                <div className="me-3">
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRef.current || !gridRef.current.api) return;

                        if (!value || value.length !== 2) {
                          gridRef.current.api.setFilterModel(null);
                          gridRef.current.api.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRef.current.api.setFilterModel({
                          createdAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                          }
                        });
                        gridRef.current.api.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>

                {isDevMode && (
                  <button
                    className="btn ripple btn-main-primary signbtn"
                    onClick={() => navigate(`/admin/cms/consent/add`)}
                  >
                    {t("btn_add_new")}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div
              className="card-header d-flex justify-content-between align-items-center"
              style={{ borderBottom: "none" }}
            >
              <h6 className="main-content-label">Consent History Audit Trail</h6>
              <div className="d-flex align-items-center">
                <div style={{ width: 300 }} className="me-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search name, email, IP, role or type..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    style={{ outline: "none", boxShadow: "none" }}
                  />
                </div>

                <div className="me-3">
                  <CustomProvider>
                    <DateRangePicker
                      style={{ width: "150px" }}
                      placeholder={t("input_placeholder_date")}
                      placement="bottomEnd"
                      onChange={(value) => {
                        if (!gridRefHistory.current || !gridRefHistory.current.api) return;

                        if (!value || value.length !== 2) {
                          gridRefHistory.current.api.setFilterModel(null);
                          gridRefHistory.current.api.onFilterChanged();
                          return;
                        }
                        const [start, end] = value;
                        gridRefHistory.current.api.setFilterModel({
                          grantedAt: {
                            type: "inRange",
                            dateFrom: moment(start).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                            dateTo: moment(end).endOf("day").format("YYYY-MM-DD HH:mm:ss")
                          }
                        });
                        gridRefHistory.current.api.onFilterChanged();
                      }}
                    />
                  </CustomProvider>
                </div>

                <button
                  className="btn ripple btn-outline-primary"
                  onClick={() => refetchHistory()}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Refresh
                </button>
              </div>
            </div>
          )}

          <div className="card-body">
            {activeTab === "documents" ? (
              isLoading ? (
                <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
              ) : (
                <div className="ag-theme-alpine" style={{ width: '100%' }}>
                  <AgGridReact {...getAgGridConfig} />
                </div>
              )
            ) : (
              isHistoryLoading ? (
                <Placeholder.Paragraph className="w-100 admin-placeholder" rowHeight={30} rowSpacing={5} active={true} rows={7} />
              ) : (
                <div className="ag-theme-alpine" style={{ width: '100%' }}>
                  <AgGridReact {...getAgGridConfigHistory} />
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsentTable;
